/**
 * Exercise — what a generator produces.
 *
 * The shape is uniform across all exercise types so the UI doesn't need
 * type-specific rendering. The UI shows the prompt, plays the audio plan,
 * presents options as buttons, and grades by string equality on `correctAnswer`.
 */

import type { AudioPlan } from "./audio-plan.js";
import type { ScaleDegreeSequence, FretPosition, PitchClass } from "@music-app/theory-engine";

export interface Exercise {
  /** The item being practiced (for FSRS tracking and replay). */
  itemId: string;
  /** Type of exercise — drives UI rendering choices. */
  exerciseType: ExerciseType;
  /** Audio plan to play before asking the question. */
  audio: AudioPlan;
  /** Human-readable question, e.g. "What scale degree did you hear?" */
  prompt: string;
  /** All answer options shown to the user. Includes the correct answer + distractors, shuffled. */
  options: AnswerOption[];
  /** The string ID of the correct option. */
  correctAnswerId: string;
  /** Free-form metadata for diagnostics, replay, debug UIs. */
  meta: ExerciseMeta;
}

export type ExerciseType =
  | "scale-degree-recognition"
  | "interval-recognition"
  | "chord-quality-recognition"
  | "chord-inversion-recognition"
  | "progression-recognition"
  | "cadence-recognition"
  | "mode-recognition"
  | "slash-chord-recognition"
  | "secondary-dominant-recognition"
  | "modulation-recognition"
  | "melodic-dictation"
  | "fret-interval-recognition"
  | "fret-triad-recognition"
  | "fret-triad-inversion-recognition"
  | "fret-caged-recognition"
  | "fret-caged-anchoring"
  | "fret-diatonic-recognition"
  | "fret-diatonic-minor-recognition"
  | "fret-seventh-recognition"
  | "fret-scale-degree-recognition"
  | "fret-scale-degree-caged-recognition"
  | "rhythmic-dictation";

export interface AnswerOption {
  /** Stable ID used for grading. */
  id: string;
  /** Primary label shown on the button. */
  label: string;
  /** Optional secondary label (e.g. letter name beneath solfège syllable). */
  sublabel?: string;
}

export interface ExerciseMeta {
  /** The key used (for ear training in key context). */
  keyName?: string;
  /** The seed used to generate this exercise — replay with the same seed. */
  seed: number;
  /** Brief explanation the UI can show after the answer. */
  explanation: string;
  /** Number of distractors used (for difficulty tuning). */
  distractorCount: number;
  /** Optional pedagogical playback shown after a wrong answer. The shape
   *  of the teach plan is generator-specific: scale-degree walks back to
   *  the tonic, intervals isolate the two notes, chords arpeggiate slower,
   *  and so on. Practice plays this once, after the wrong-answer chime. */
  teachAudio?: AudioPlan;
  /**
   * MIDI value of the key tonic. Set by generators that establish a
   * tonal center (scale-degree today; intervals when vocal grading
   * arrives). Vocal mode in Practice uses this to play the reference
   * pitch before recording, and as input to gradeScaleDegree.
   */
  tonicMidi?: number;
  /**
   * Mode for the key tonic — "major" or "minor". Same generators that
   * set `tonicMidi`. Used by gradeScaleDegree and the vocal/MIDI graders.
   */
  tonalityMode?: "major" | "minor";
  /**
   * For dictation exercises: the expected note sequence as scale
   * degrees (octave-agnostic by default; see `ScaleDegree.octaveOffset`
   * for register info). The grader (`gradeDictation`) compares the
   * user's submitted sequence against this.
   *
   * Phase 2A spec §8.2 floats this on `Exercise` itself; we keep it on
   * `meta` to match where every other generator-specific datum already
   * lives (`teachAudio`, `tonicMidi`, `tonalityMode`, etc.).
   */
  expectedSequence?: ScaleDegreeSequence;
  /**
   * For rhythmic dictation: the expected attack pattern as a binary string,
   * e.g. "10101010" (4/4 eighth grid, 8 cells). The grader reads this.
   */
  expectedPattern?: string;
  /** For rhythmic dictation: the tempo in BPM chosen by the generator. */
  rhythmTempo?: number;
  /** For rhythmic dictation: the meter of the exercise. */
  rhythmMeter?: "4/4" | "6/8";
  /**
   * For dictation: whether the generated melody allows chromatic
   * neighbors. Populated from `MelodicDictationParams.chromatic` so the
   * UI's `<DictationInput>` knows whether to surface the chromatic
   * syllable row (di / ri / fi / le / te). Set by the
   * `melodic-dictation` generator only.
   */
  chromaticEnabled?: boolean;
  /**
   * For dictation: melodic range hint, "narrow" (within a 5th) or
   * "wide" (up to an octave). Drives the piano-mode keyboard's default
   * span. Set by the `melodic-dictation` generator only.
   */
  range?: "narrow" | "wide";
  /**
   * For the guitar fretboard exercises: the tap-to-answer prompt. Carries
   * the pre-lit context, the accepted-position set, and one canonical
   * solution voicing. Set only by the `fret-*-recognition` generators; the
   * UI branches on `exerciseType` to render `<Fretboard>` instead of
   * option buttons, and `gradeFretboard` reads it. See
   * docs/design/guitar-harmony-v1.md §6.1.
   */
  fretboard?: FretboardPrompt;
  /**
   * For fretboard exercises: the exact MIDI notes the exercise sounded, in
   * ascending order. The Arpeggio/Block replay controls play these so they
   * match what was heard on first play. Necessary because the on-neck voicing
   * (`fretboard.given`/`solution`) is chosen for playability and can sit an
   * octave away from the audio register — reconstructing playback from the
   * fret positions would sound the right pitch classes in the wrong octave.
   * When absent, the UI falls back to the fret-derived notes.
   */
  playbackMidis?: number[];
}

/**
 * The answer contract for a fretboard exercise. The generator builds this
 * from the item `params` + the randomized key + the pure `fretboard.ts`
 * helpers — frets are computed here, never stored on the item.
 *
 * See docs/design/guitar-harmony-v1.md §6.1.
 */
export interface FretboardPrompt {
  /** v1: only standard tuning. The grader resolves this tag to STANDARD_TUNING. */
  tuning: "standard";
  /**
   * Positions pre-lit as context the user is answering against — e.g. the
   * heard root for an interval item. Rendered as `given`; not tappable.
   */
  given: FretPosition[];
  /**
   * Restrict valid taps to these string indices (e.g. one string set).
   * Omitted ⇒ the whole neck is fair game (interval node).
   */
  allowedStrings?: number[];
  /** How many DISTINCT pitches the user must place. 1 = interval, 3 = triad. */
  notesToPlace: number;
  /**
   * The exact set of positions that count. A tap is valid iff it appears
   * here. Constructed by the generator so it contains ONLY positions of the
   * required note(s):
   *   - interval node: every position of the single target pitch
   *     (rootMidi + intervalSemitones) within `allowedStrings`.
   *   - triad node: every position on the string set whose pitch class is
   *     one of {root, 3rd, 5th}.
   */
  acceptedPositions: FretPosition[];
  /**
   * Inversion identity. When set, the LOWEST-MIDI tapped position must have
   * this pitch class (the bass note IS the inversion). Set by the inversion
   * node only; undefined for the interval & triad-shape nodes.
   */
  bassPitchClass?: PitchClass;
  /**
   * One canonical voicing to flash on a wrong answer. Display only — grading
   * never reads this.
   */
  solution: FretPosition[];
  /**
   * Narrow fret window for Listen mode — the 4-5 fret range that contains
   * both the root and the interval note for this shape. The UI passes this to
   * `<Fretboard fretWindow>` in Listen mode so the eye doesn't dart across the
   * full neck while the shape auto-advances. Drill/Practice ignores this and
   * shows the full neck. Set by the `fret-interval-recognition` generator only.
   */
  listenFretWindow?: { from: number; to: number };
}

/* ─── Seeded RNG ──────────────────────────────────────────────────────────── */

/**
 * Deterministic pseudo-random generator — Mulberry32.
 * Same seed → same sequence, always. Critical for reproducible exercises.
 */
export class Rng {
  private state: number;
  constructor(seed: number) {
    this.state = seed >>> 0;
  }
  /** Float in [0, 1). */
  next(): number {
    let t = (this.state += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  /** Integer in [0, n). */
  int(n: number): number {
    return Math.floor(this.next() * n);
  }
  /** Pick one element from a non-empty array. */
  pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) throw new Error("Rng.pick: empty array");
    return arr[this.int(arr.length)]!;
  }
  /** Fisher-Yates shuffle, returns a new array. */
  shuffle<T>(arr: readonly T[]): T[] {
    const out = [...arr];
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      [out[i], out[j]] = [out[j]!, out[i]!];
    }
    return out;
  }
  /** Pick `n` distinct elements without replacement. */
  sample<T>(arr: readonly T[], n: number): T[] {
    const k = Math.min(n, arr.length);
    return this.shuffle(arr).slice(0, k);
  }
}
